import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { SpamClassification, WorkflowEmailPreview } from "@/lib/workflowRunReview";
import { Loader2, Mail, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";

interface WorkflowApprovalReviewCardProps {
  email: WorkflowEmailPreview;
  verdict: SpamClassification | null;
  loading?: boolean;
  writeScopeMissing?: boolean;
  onKeepEmail: () => void;
  onMoveToTrash: () => void;
  onReconnectGmail?: () => void;
}

function formatDate(date?: string): string {
  if (!date) return "";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return date;
  }
}

export function WorkflowApprovalReviewCard({
  email,
  verdict,
  loading = false,
  writeScopeMissing = false,
  onKeepEmail,
  onMoveToTrash,
  onReconnectGmail,
}: WorkflowApprovalReviewCardProps) {
  const markedSpam = verdict?.spam === true;

  return (
    <div className="flex flex-col gap-4 p-4 border-b bg-muted/20">
      <div>
        <h4 className="text-sm font-semibold">Review email before trashing</h4>
        <p className="text-xs text-muted-foreground mt-1">
          {markedSpam
            ? "The classifier marked this as spam. If this email is important to you, choose Keep email."
            : "Confirm whether this email should be moved to Gmail Trash."}
        </p>
      </div>

      <div
        className={`rounded-lg border p-3 space-y-2 ${
          markedSpam
            ? "border-destructive/40 bg-destructive/5"
            : "border-green-500/40 bg-green-500/5"
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          {markedSpam ? (
            <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
          ) : (
            <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" />
          )}
          <span className="font-semibold text-sm">
            {markedSpam ? "Marked as spam" : verdict ? "Not spam" : "Classification pending"}
          </span>
          {verdict?.confidence != null && (
            <Badge variant="outline" className="text-xs">
              {Math.round(verdict.confidence * 100)}% confidence
            </Badge>
          )}
        </div>
        {verdict?.reason && (
          <div className="text-sm">
            <span className="font-medium text-muted-foreground">Why: </span>
            <span>{verdict.reason}</span>
          </div>
        )}
        {markedSpam && (
          <p className="text-xs text-muted-foreground">
            Support, billing, and vendor emails from services you use (e.g. mailbox providers,
            help desks) are usually not spam. Only trash if you agree this is unwanted mail.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-background p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium text-sm break-words">{email.subject}</p>
            <p className="text-xs text-muted-foreground">
              From: {email.from || "Unknown"}
              {email.to ? ` · To: ${email.to}` : ""}
              {email.date ? ` · ${formatDate(email.date)}` : ""}
            </p>
          </div>
        </div>
        <ScrollArea className="max-h-[200px] rounded-md border bg-muted/30 p-3">
          <div className="text-sm whitespace-pre-wrap break-words">{email.body}</div>
        </ScrollArea>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" disabled={loading} onClick={onKeepEmail}>
          Keep email
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={loading}
          onClick={onMoveToTrash}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5 mr-1" />
          )}
          Move to trash
        </Button>
        {writeScopeMissing && onReconnectGmail && (
          <Button size="sm" variant="outline" disabled={loading} onClick={onReconnectGmail}>
            Reconnect Gmail
          </Button>
        )}
      </div>
      {writeScopeMissing && (
        <p className="text-xs text-destructive">
          Gmail modify permission is required to move emails to trash.
        </p>
      )}
    </div>
  );
}
